import React, { useState, useEffect } from 'react';
import Styles from '../../assets/css/components/logistic/logisticEdit.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import OpenEye from '../../assets/images/Auth/visibility.png';
import CloseEye from '../../assets/images/Auth/visibility_off.png';
import { useDispatch } from 'react-redux';
import { popUpBoxTrigger } from "../../features/message/popUpSlice";
import { setShouldRefresh } from '../../features/logistic/logisticSlice';
import PopUpMessage from "../../utils/errors/PopUpMessage";
import ValidationHandler from '../../utils/validationHandler';
import { getLogisticById, updateLogistic, warehouseList } from '../../services/Api';
import { AiOutlineLoading } from 'react-icons/ai';
import Loader from '../../utils/Loader';

const LogisticEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [originalData, setOriginalData] = useState(null);
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [warehouses, setWarehouses] = useState([]);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone_number: '',
        warehouse_id: '',
        password: '',
        confirm_password: '',
        status: '',
        warehouse_name: ''
    });

    const [errors, setErrors] = useState({});

    // Validation rules
    const validationRules = {
        name: {
            required: true,
            message: 'Please enter a name'
        },
        email: {
            required: true,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: 'Please enter a valid email address'
        },
        phone_number: {
            required: true,
            pattern: /^\d{10}$/,
            message: 'Please enter a valid 10-digit phone number'
        },
        warehouse_id: {
            required: true,
            message: 'Please select a warehouse'
        },
        status: {
            required: true,
            message: 'Please select a status'
        },
        password: {
            minLength: 6,
            message: 'Password must be at least 6 characters'
        },
        confirm_password: {
            match: 'password',
            message: 'Passwords do not match'
        }
    };

    // Fetch logistic data on component mount
    useEffect(() => {
        const fetchLogisticData = async () => {
            setIsLoading(true);
            try {
                const response = await getLogisticById(id);
                if (response.status === 200) {
                    const data = response.data.data;
                    setOriginalData(data);
                    setFormData({
                        name: data.name || '',
                        email: data.email || '',
                        phone_number: data.phone_number || '',
                        warehouse_id: data.warehouse_id ? data.warehouse_id.toString() : '',
                        status: data.status === 1 ? '1' : '0',
                        password: data.password || '',
                        confirm_password: data.password || '',
                        warehouse_name: data.warehouse?.name || ''
                    });
                }
            } catch (error) {
                showPopup('error', 'Error', 'Failed to fetch logistic details');
            } finally {
                setIsLoading(false);
            }
        };
        fetchLogisticData();
    }, [id]);

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const response = await warehouseList();
                setWarehouses(response.data);
            } catch (error) {
                console.error("Error fetching warehouses:", error);
            }
        };
        fetchWarehouses();
    }, []);

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    const updateData = {
        id: id,
        name: formData.name,
        email: formData.email,
        phone_number: formData.phone_number,
        warehouse_id: formData.warehouse_id,
        status: formData.status,
        password: formData.password,
        password_confirmation: formData.confirm_password
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "phone_number" && !/^\d*$/.test(value)) {
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        const selectElement = e.target;
        if (value) {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#797775';
        }

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        if (name === "password") {
            if (formData.confirm_password && value !== formData.confirm_password) {
                setErrors(prev => ({ ...prev, confirm_password: "Passwords do not match" }));
            } else {
                setErrors(prev => ({ ...prev, confirm_password: "" }));
            }
        }

        if (name === "confirm_password") {
            if (value !== formData.password) {
                setErrors(prev => ({ ...prev, confirm_password: "Passwords do not match" }));
            } else {
                setErrors(prev => ({ ...prev, confirm_password: "" }));
            }
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const updateData = {
                id: id,
                name: formData.name,
                email: formData.email,
                phone_number: formData.phone_number,
                warehouse_id: formData.warehouse_id,
                status: formData.status,
                password: formData.password || null,
                password_confirmation: formData.confirm_password || null
            };

            // Validate that password is provided if the user wants to change it
            if (formData.password && formData.password !== formData.confirm_password) {
                setErrors(prev => ({ ...prev, confirm_password: "Passwords do not match" }));
                setLoading(false);
                return;
            }

            const response = await updateLogistic(updateData);

            if (response.status === 200) {
                showPopup('success', 'Success', 'Logistic staff updated successfully');
                dispatch(setShouldRefresh(true));
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate("/logistic");
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to update logistic staff';
            showPopup('error', 'Error', errorMessage);

            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={Styles.logistic_edit}>
            <PopUpMessage />
            <div className={Styles.heading}>
                <h2>Logistic Information</h2>
            </div>

            <div className={Styles.logistic_form}>
                {isLoading ? (
                    <div className={`${Styles.form_loader_container}`}>
                        <div>
                            <Loader size="large" color="primary" />
                        </div>
                    </div>
                ) : (
                <div className={Styles.logistic_details}>
                    <div className={Styles.title}>
                        <h4>Logistic details</h4>
                        <hr />
                    </div>
                    <div className={Styles.form}>
                        <div className={`${Styles.label_input} ${errors.name ? Styles.error : ''}`}>
                            <label>Name<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter Name"
                                />
                                {errors.name && <p className={`${Styles.errorMessage}`}>{errors.name}</p>}
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${errors.email ? Styles.error : ''}`}>
                            <label>Email<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter Email"
                                />
                                {errors.email && <p className={`${Styles.errorMessage}`}>{errors.email}</p>}
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${errors.phone_number ? Styles.error : ''}`}>
                            <label>Phone number<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <input
                                    type="text"
                                    name="phone_number"
                                    value={formData.phone_number}
                                    onChange={handleInputChange}
                                    placeholder="Enter Phone Number"
                                    maxLength={10}
                                />
                                {errors.phone_number && <p className={`${Styles.errorMessage}`}>{errors.phone_number}</p>}
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${errors.status ? Styles.error : ''}`}>
                            <label>Status<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                >
                                    <option value="" disabled hidden>Select Status</option>
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </select>
                                {errors.status && <p className={`${Styles.errorMessage}`}>{errors.status}</p>}
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${errors.warehouse_id ? Styles.error : ''}`}>
                            <label>Warehouse Name<span>*</span></label>
                            <select
                                name="warehouse_id"
                                value={formData.warehouse_id}
                                onChange={handleInputChange}
                            >
                                <option value="" disabled hidden>Select Warehouse Name</option>
                                {warehouses.filter(warehouse => warehouse.status === 1).map((warehouse) => ( // Filter for active warehouses
                                    <option key={warehouse.id} value={warehouse.id}>
                                        {warehouse.name}
                                    </option>
                                ))}
                            </select>
                            {errors.warehouse_id && <span className={Styles.errorMessage}>{errors.warehouse_id}</span>}
                        </div>

                        {/* <div className={`${Styles.label_input} ${errors.password ? Styles.error : ''}`}>
                            <label>Password</label>
                            <div className={Styles.password_field}>
                                <input
                                    name="password"
                                    type={newPasswordVisible ? "text" : "password"}
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    placeholder="Enter new password (optional)"
                                />
                                <img
                                    src={newPasswordVisible ? OpenEye : CloseEye}
                                    onClick={() => setNewPasswordVisible(!newPasswordVisible)}
                                    alt="Toggle visibility"
                                    className={Styles.eye_icon}
                                />
                            </div>
                            {errors.password && <span className={Styles.errorMessage}>{errors.password}</span>}
                        </div> */}

                        {/* <div className={`${Styles.label_input} ${errors.confirm_password ? Styles.error : ''}`}>
                            <label>Confirm Password</label>
                            <div className={Styles.password_field}>
                                <input
                                    name="confirm_password"
                                    type={confirmPasswordVisible ? "text" : "password"}
                                    value={formData.confirm_password}
                                    onChange={handleInputChange}
                                    placeholder="Confirm new password (optional)"
                                />
                                <img
                                    src={confirmPasswordVisible ? OpenEye : CloseEye}
                                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                                    alt="Toggle visibility"
                                    className={Styles.eye_icon}
                                />
                            </div>
                            {errors.confirm_password && <span className={Styles.errorMessage}>{errors.confirm_password}</span>}
                        </div> */}
                    </div>

                    <div className={`${Styles.buttons}`}>
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading  ? <AiOutlineLoading /> : 'Update'}
                        </button>
                        <button
                            onClick={() => navigate("/logistic")}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
                )}
            </div>
        </div>
    );
};

export default LogisticEdit;